import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import api from "../../../../common/api";
import toastCommonProps from "../../../../common/toast";
import { generateRandomNickname } from "../../../../common/NickNameValue";
import { saveLoginInfo } from "../../../../features/mobile/commonSlice";
import { getNickname } from "../../../../features/mobile/questionHealthSlice";
import AgreementDialog from "../../components/sign/AgreementDialog";
import HeadCont from "../../components/common/HeadCont";
import TitleBox from "../../components/common/TitleBox";
import InputBox from "../../components/common/InputBox";
import CharacterInput from "../../components/sign/CharacterInput";
import Btn from "../../components/common/Btn";

const GoogleSignUp = () => {
  const { t } = useTranslation('auth');
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();

  // Check if resuming from incomplete registration
  const resumeStep = searchParams.get('resumeStep');
  const resumeUserId = searchParams.get('userId');

  const [step, setStep] = useState(resumeStep ? parseInt(resumeStep) : 1); // 1: Terms, 2: Nickname, 3: Character
  const [openDialog, setOpenDialog] = useState(resumeStep ? false : true);
  const [googleUserData, setGoogleUserData] = useState(null);
  const [userId, setUserId] = useState(resumeUserId || null);
  const [nickName, setNickName] = useState("");
  const [characterPhoto, setCharacterPhoto] = useState("");
  const [active, setActive] = useState("ready");
  const [errorMsg, setErrorMsg] = useState("");
  const dummyNickname = useSelector((state) => state.questionHealthApp?.dummyNickname) || generateRandomNickname();

  useEffect(() => {
    // If resuming from incomplete registration, user data is already in URL params
    if (resumeStep) {
      const email = searchParams.get('email');
      const name = searchParams.get('nickName') || searchParams.get('name');
      const photo = searchParams.get('photo');

      setGoogleUserData({
        email,
        name,
        photo: photo || '',
        googleId: '' // Not needed for resume
      });

      // Set button to enabled for nickname step
      setActive("on");
      return;
    }

    // Parse Google user data from URL for new signup
    const userDataParam = searchParams.get('userData');
    const email = searchParams.get('email');
    const name = searchParams.get('name');
    const photo = searchParams.get('photo');

    if (userDataParam) {
      try {
        const userData = JSON.parse(decodeURIComponent(userDataParam));
        setGoogleUserData(userData);
      } catch (error) {
        console.error('Failed to parse Google user data:', error);
        toast(<p>Failed to process Google login data</p>, toastCommonProps("bottom-center", "toast_alert", 1500));
        navigate('/sign');
      }
    } else if (email && name) {
      // Fallback: construct from individual params
      setGoogleUserData({
        email,
        name,
        photo: photo || '',
        googleId: '' // This might be missing, but backend will handle it
      });
    } else {
      toast(<p>Invalid Google login data</p>, toastCommonProps("bottom-center", "toast_alert", 1500));
      navigate('/sign');
    }
  }, [searchParams, navigate, resumeStep]);

  const onSaveAgreement = async (items) => {
    if (!googleUserData) {
      toast(<p>Missing Google user data</p>, toastCommonProps("bottom-center", "toast_alert", 1500));
      return;
    }

    try {
      const firebaseToken = window.sessionStorage.getItem("token") || null;

      const signupParams = {
        googleId: googleUserData.googleId,
        email: googleUserData.email,
        name: googleUserData.name,
        photo: googleUserData.photo,
        agree: items,
        firebaseToken: firebaseToken
      };

      const res = await api.post('/auth/google/complete-signup', signupParams);

      if (res.data.ok) {
        // Save token and user info
        localStorage.setItem('accessToken', res.data.accessToken);
        setUserId(res.data.userId);

        dispatch(saveLoginInfo({
          userId: res.data.userId,
          nickName: res.data.nickName || googleUserData.name.split(' ')[0],
          email: res.data.email,
          existPass: false, // Google accounts don't have passwords
        }));

        toast(<p>{t('signup.success')}</p>, toastCommonProps("bottom-center", "toast_success", 1500));

        // Generate nickname for later steps
        try {
          dispatch(getNickname({ maxLength: 12 }));
        } catch (error) {
          console.log('getNickname not available, using fallback');
        }

        // Close dialog and move to nickname step
        setOpenDialog(false);
        setStep(2);
        setActive("on"); // Nickname is optional, so button is always enabled
      } else {
        toast(<p>{res.data.error || t('signup.error_occurred')}</p>, toastCommonProps("bottom-center", "toast_alert", 2000));
      }
    } catch (err) {
      console.error('Google signup error:', err);
      const errorMsg = err.response?.data?.error || t('signup.error_occurred');
      toast(<p>{errorMsg}</p>, toastCommonProps("bottom-center", "toast_alert", 2000));
    }
  };

  const onChangeInput = (e) => {
    setNickName(e.target.value);
    // Nickname step is optional, so button is always enabled
    setActive("on");
  };

  const onSaveEach = (type, value) => {
    if (type === 'characterPhoto') {
      setCharacterPhoto(value);
      setActive("on");
    }
  };

  const onSetNextStep = async () => {
    if (step === 2) {
      // Nickname step - move to character
      setStep(3);
      setActive("ready");
    } else if (step === 3) {
      // Character step - update profile and go to home
      try {
        const finalNickName = nickName.trim() || dummyNickname;

        const res = await api.post('/user/updateProfile', {
          userId: userId,
          nickName: finalNickName,
          characterPhoto: characterPhoto
        });

        console.log('Update profile response:', res.data);

        if (res.data.ok) {
          // Update Redux state with the final nickname and character
          dispatch(saveLoginInfo({
            userId: userId,
            nickName: finalNickName,
            email: googleUserData?.email,
            characterPhoto: characterPhoto,
            existPass: false,
          }));

          toast(<p>{t('signup.success')}</p>, toastCommonProps("bottom-center", "toast_success", 1500));

          // Navigate to home after a short delay to ensure Redux state is saved
          setTimeout(() => {
            navigate('/home');
          }, 1600);
        } else {
          toast(<p>{res.data.error || t('signup.error_occurred')}</p>, toastCommonProps("bottom-center", "toast_alert", 2000));
        }
      } catch (err) {
        console.error('Profile update error:', err);
        toast(<p>{t('signup.error_occurred')}</p>, toastCommonProps("bottom-center", "toast_alert", 2000));
      }
    }
  };

  return (
    <>
      <HeadCont title={t('signup.title')} />

      {step === 1 ? (
        // Step 1: Terms & Agreements
        <div className="px-4 pt-20">
          <h1 className="text-h2 text-gray_180 mb-2">
            {t('signup.welcome')}
          </h1>
          {googleUserData && (
            <p className="text-body2 text-gray_160 mb-6">
              {googleUserData.email}
            </p>
          )}

          {openDialog && (
            <AgreementDialog
              openDialog={openDialog}
              onClose={() => {
                setOpenDialog(false);
                navigate('/sign');
              }}
              onSaveAgreement={onSaveAgreement}
            />
          )}
        </div>
      ) : step === 2 ? (
        // Step 2: Nickname
        <section className="mx-6 mt-[80px] flex flex-col gap-6">
          <TitleBox option={2} title={t('signup.choose_nickname')} />
          <InputBox
            option="common"
            nameTitle={"nickName"}
            placeholder={dummyNickname}
            comments={t('signup.nickname_display')}
            onChangeInput={onChangeInput}
            value={nickName}
            setValue={setNickName}
            setErrorMsg={setErrorMsg}
            active={active}
            errorMsg={errorMsg}
          />
          <p className="text-sm text-gray-500 -mt-2">
            {t('signup.nickname_optional', 'Leave blank to use auto-generated nickname')}
          </p>
        </section>
      ) : (
        // Step 3: Character
        <section className="mx-6 mt-[80px] flex flex-col gap-6">
          <TitleBox option={2} title={t('signup.choose_character')} />
          <CharacterInput
            onSaveEach={onSaveEach}
            character={characterPhoto}
          />
        </section>
      )}

      {/* Show button for nickname and character steps */}
      {step !== 1 && (
        <Btn
          active={active}
          onAction={onSetNextStep}
          option={"bottom"}
          bgColor={"bg-main"}
          textColor={"text-white"}
          borderColor={"border border-main"}
          title={step === 2 ? t('signup.continue') : t('signup.complete')}
        />
      )}
    </>
  );
};

export default GoogleSignUp;
